import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { X, Upload, Loader2, Search, Copy, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Media picker modal: browse the media library, upload new images, pick one.
 * Props:
 *  - onPick(url): called when user selects an image
 *  - onClose(): close without pick
 *  - folder: default upload folder (string)
 */
export default function MediaPicker({ onPick, onClose, folder = "misc" }) {
  const [data, setData] = useState({ items: [], count: 0, folders: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [activeFolder, setActiveFolder] = useState("");
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);

  const load = async (f = activeFolder) => {
    setLoading(true);
    try {
      const { data } = await api.get("/media", { params: f ? { folder: f } : {} });
      setData(data);
    } catch { toast.error("Impossible de charger la médiathèque"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFolder]);

  const filtered = (data.items || []).filter((it) =>
    !q || it.name.toLowerCase().includes(q.toLowerCase()) || it.folder.toLowerCase().includes(q.toLowerCase())
  );

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", activeFolder || folder);
      const { data: r } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploadée");
      await load();
      if (onPick) onPick(r.url);
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url); setTimeout(() => setCopied(null), 1500);
    toast.success("URL copiée");
  };

  const del = async (url) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    try {
      await api.delete("/media", { params: { url } });
      toast.success("Supprimé"); load();
    } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" onClick={onClose} data-testid="media-picker">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-0.5">Médiathèque</div>
            <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">{data.count} fichier(s) · {Math.round((data.total_size || 0) / 1024)} Ko</h3>
          </div>
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-[color:var(--brand-navy)] font-bold" data-testid="media-picker-upload">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Uploader
            </Button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[hsl(var(--muted))] flex items-center justify-center" data-testid="media-picker-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-[hsl(var(--border))] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom…" className="pl-9 h-9" />
          </div>
          <button onClick={() => setActiveFolder("")} className={`px-3 h-8 rounded-full text-xs font-semibold border ${!activeFolder ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>Tous</button>
          {(data.folders || []).map((f) => (
            <button key={f} onClick={() => setActiveFolder(f)} className={`px-3 h-8 rounded-full text-xs font-semibold border ${activeFolder === f ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>{f}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="py-20 flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucun média. Uploadez votre premier fichier.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {filtered.map((m) => (
                <div key={m.url} className="group relative rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--muted))]/30 hover:border-[color:var(--brand-sky)] hover:shadow-md transition cursor-pointer" data-testid={`media-item-${m.name}`}>
                  <div className="aspect-square" onClick={() => onPick?.(m.url)}>
                    <img src={m.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="px-2.5 py-2">
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono truncate">{m.folder}/</div>
                    <div className="text-[11px] font-semibold truncate text-[color:var(--brand-navy)]">{m.name}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{Math.round(m.size_bytes / 1024)} Ko</div>
                  </div>
                  <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={(e) => { e.stopPropagation(); copyUrl(m.url); }} className="w-7 h-7 rounded-md bg-white/95 hover:bg-white shadow text-[color:var(--brand-navy)] flex items-center justify-center" title="Copier l'URL">
                      {copied === m.url ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); del(m.url); }} className="w-7 h-7 rounded-md bg-white/95 hover:bg-red-50 shadow text-red-600 flex items-center justify-center" title="Supprimer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
